import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

/**
 * Shown on every page until the logged-in user confirms they are 18+.
 * Required because Phase B will introduce paid play and a real-money prize fund.
 */
export default function AgeConfirmDialog() {
  const { user, confirmAge, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const open = Boolean(user) && user.age_confirmed_18 === false;

  const handleConfirm = async () => {
    setBusy(true);
    const res = await confirmAge();
    setBusy(false);
    if (res.ok) {
      toast.success("Age confirmed");
    } else {
      toast.error(res.error || "Could not save your confirmation");
    }
  };

  const handleDecline = async () => {
    await logout();
    toast.info("You have been signed out");
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* not dismissible */ }}>
      <DialogContent
        data-testid="age-confirm-dialog"
        className="sm:max-w-md"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-zinc-950">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <DialogTitle className="font-heading text-2xl tracking-tight">Quick age check</DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="text-sm text-zinc-600 mt-2 space-y-2">
              <p>
                Dugsy&apos;s Football Challenge is being prepared for a paid-entry league that includes real-money prizes. We need to confirm everyone with an account is 18 or over before that launches.
              </p>
              <p className="font-semibold text-zinc-950">
                Please confirm you are 18 years of age or older.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={handleDecline}
            data-testid="age-confirm-decline"
            className="h-11 rounded-sm border-zinc-300"
          >
            I am under 18
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={handleConfirm}
            data-testid="age-confirm-accept"
            className="h-11 rounded-sm bg-zinc-950 hover:bg-zinc-900 text-white"
          >
            I confirm I am 18 or over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
